import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useAppDispatch } from '../app/hooks'
import { selectIsAuthenticated, logoutAsync } from '../features/authentication/authenticationSlice'
import { ROUTES } from '../routes'
import ThemeToggle, { ThemeMode } from './ToggleSwitch'

const Header = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true' && import.meta.env.VITE_DEBUG_MODE === 'true';
  const showAuthenticatedUI = isAuthenticated || bypassAuth;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<ThemeMode>('auto');

  const handleLogout = async () => {
    await dispatch(logoutAsync());
    navigate(ROUTES.LOGIN);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  // Load saved theme preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      setThemeMode(savedTheme);
    }
  }, []);

  // Apply theme changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Save theme preference
    localStorage.setItem('theme', themeMode);
    
    if (themeMode === 'auto') {
      // Auto mode: follow system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const applyAutoTheme = () => {
        if (mediaQuery.matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      };
      
      // Apply immediately
      applyAutoTheme();
      
      // Listen for system theme changes
      mediaQuery.addEventListener('change', applyAutoTheme);
      
      return () => {
        mediaQuery.removeEventListener('change', applyAutoTheme);
      };
    } else if (themeMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [themeMode]);

  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
  };

  useEffect(() => {
    // Initialize mobile menu functionality
    const initializeMobile = () => {
      // Select main DOM elements for mobile menu
      const menu = document.querySelector("#mobile-menu")
      const overlay = document.querySelector("#mobile-menu-overlay")
      const burger = document.querySelector("#burger")
      const body = document.querySelector("body")

      // Define mobile breakpoint for responsive behavior
      const MOBILE_BREAKPOINT = 1024

      // Exit if required elements are missing
      if (!menu || !burger || !body || !overlay) return

      // ============================
      // MENU TOGGLE FUNCTIONALITY
      // ============================

      const updateMenuState = (isOpen: boolean) => {
        burger.setAttribute("aria-expanded", isOpen.toString())
        burger.classList.toggle("active", isOpen)

        menu.classList.toggle("is-open", isOpen)
        menu.classList.toggle("-translate-x-full", !isOpen)
        menu.classList.toggle("translate-x-0", isOpen)

        overlay.classList.toggle("opacity-0", !isOpen)
        overlay.classList.toggle("opacity-100", isOpen)
        overlay.classList.toggle("pointer-events-none", !isOpen)
        overlay.classList.toggle("pointer-events-auto", isOpen)

        body.classList.toggle("overflow-hidden", isOpen)
      }

      const handleBurgerClick = () => {
        const isOpening = !menu.classList.contains("is-open")
        updateMenuState(isOpening)
      }

      const handleOverlayClick = (event: Event) => {
        if (event.target === overlay) updateMenuState(false)
      }

      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && menu.classList.contains("is-open")) {
          updateMenuState(false)
        }
        if (event.key === "Escape") {
          setIsDropdownOpen(false)
        }
      }

      const handleWindowResize = () => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) {
          updateMenuState(false)
        }
      }

      // Close dropdown when clicking outside
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Element
        const profileButton = document.getElementById('profile-dropdown-button')
        const dropdown = document.getElementById('profile-dropdown')
        
        if (profileButton && dropdown && !profileButton.contains(target) && !dropdown.contains(target)) {
          setIsDropdownOpen(false)
        }
      }

      // Add event listeners for main menu
      burger.addEventListener("click", handleBurgerClick)
      overlay.addEventListener("click", handleOverlayClick)
      document.addEventListener("keydown", handleEscapeKey)
      document.addEventListener("mousedown", handleClickOutside)
      window.addEventListener("resize", handleWindowResize)

      burger.setAttribute("aria-expanded", "false")
      handleWindowResize()

      // Cleanup function
      return () => {
        burger.removeEventListener("click", handleBurgerClick)
        overlay.removeEventListener("click", handleOverlayClick)
        document.removeEventListener("keydown", handleEscapeKey)
        document.removeEventListener("mousedown", handleClickOutside)
        window.removeEventListener("resize", handleWindowResize)
      }
    }

    const cleanup = initializeMobile()
    return cleanup
  }, [])
  return (
  <header className="shadow-header dark:bg-primary-10 sticky top-0 left-0 z-10 bg-white px-4 py-3 lg:px-10">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-2 lg:flex lg:justify-between lg:gap-4 h-[52px]">
        {/* Mobile:Burger & Search */}
        <div className="flex items-center gap-2 lg:hidden">
          <button id="burger">
            <svg
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="fill-grey-30 dark:fill-grey-80"
                d="M21 19C21.5523 19 22 19.4477 22 20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20C2 19.4477 2.44772 19 3 19H21ZM21 11C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H3C2.44772 13 2 12.5523 2 12C2 11.4477 2.44772 11 3 11H21ZM21 2C21.5523 2 22 2.44772 22 3C22 3.55228 21.5523 4 21 4H3C2.44772 4 2 3.55228 2 3C2 2.44772 2.44772 2 3 2H21Z"
              />
            </svg>
          </button>
          {showAuthenticatedUI && (
            <button>
              <svg
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="fill-grey-30 dark:fill-grey-80"
                  d="M10 2C14.4183 2 18 5.58172 18 10C18.0022 11.7767 17.4066 13.5028 16.3096 14.9004L21.71 20.29C21.8993 20.4778 22.0059 20.7334 22.0059 21C22.0059 21.2666 21.8993 21.5222 21.71 21.71C21.5222 21.8993 21.2666 22.0059 21 22.0059C20.7334 22.0059 20.4778 21.8993 20.29 21.71L14.9004 16.3096C13.5028 17.4066 11.7767 18.0022 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2ZM10 4C6.68629 4 4 6.68629 4 10C4 13.3137 6.68629 16 10 16C13.3137 16 16 13.3137 16 10C16 6.68629 13.3137 4 10 4Z"
                />
              </svg>
            </button>
          )}
        </div>
        {/* Logo */}
        <Link to="/" className="lg:w-[224px]">
          <svg
            width={139}
            height={23}
            viewBox="0 0 139 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="fill-grey-20 dark:fill-white"
              d="M4.848 17.096C3.984 17.096 3.28 16.864 2.736 16.4C2.192 15.936 1.92 15.232 1.92 14.288V7.088H0.216V5.984C1 5.68 1.696 5.28 2.304 4.784C2.928 4.272 3.424 3.672 3.792 2.984H5.352V5.528H8.424V7.088H5.328V14.096C5.328 14.544 5.432 14.872 5.64 15.08C5.848 15.272 6.128 15.368 6.48 15.368C6.752 15.368 7.04 15.312 7.344 15.2C7.664 15.088 7.952 14.944 8.208 14.768L8.688 15.608C8.192 16.056 7.592 16.416 6.888 16.688C6.2 16.96 5.52 17.096 4.848 17.096ZM9.65363 16.016C9.95763 15.952 10.1896 15.88 10.3496 15.8C10.5256 15.72 10.7096 15.592 10.9016 15.416V8.504C10.9016 7.928 10.7896 7.536 10.5656 7.328C10.3576 7.104 9.97363 6.976 9.41363 6.944L9.48563 6.008C9.72563 5.96 10.1576 5.888 10.7816 5.792C12.4936 5.504 13.5256 5.304 13.8776 5.192L14.2856 5.408V15.416C14.6856 15.72 15.1976 15.936 15.8216 16.064L15.7256 17H9.55762L9.65363 16.016ZM12.3656 3.752C11.7256 3.752 11.2456 3.608 10.9256 3.32C10.6056 3.032 10.4456 2.616 10.4456 2.072C10.4456 0.951999 11.0856 0.391999 12.3656 0.391999C12.9896 0.391999 13.4616 0.535999 13.7816 0.823999C14.1016 1.112 14.2616 1.528 14.2616 2.072C14.2616 2.616 14.1016 3.032 13.7816 3.32C13.4616 3.608 12.9896 3.752 12.3656 3.752ZM17.0364 16.016C17.3404 15.952 17.5724 15.88 17.7324 15.8C17.9084 15.72 18.1004 15.592 18.3084 15.416V8.504C18.3084 7.928 18.1964 7.536 17.9724 7.328C17.7484 7.104 17.3564 6.976 16.7964 6.944L16.8684 6.008L17.6604 5.864C19.3564 5.576 20.4604 5.352 20.9724 5.192L21.4044 5.408L21.5964 6.8C22.0124 6.288 22.5084 5.896 23.0844 5.624C23.6764 5.336 24.3244 5.192 25.0284 5.192C25.7964 5.192 26.4604 5.368 27.0204 5.72C27.5964 6.056 28.0204 6.536 28.2924 7.16C28.6924 6.552 29.2124 6.072 29.8524 5.72C30.4924 5.368 31.2044 5.192 31.9884 5.192C33.0764 5.192 33.9484 5.52 34.6044 6.176C35.2604 6.832 35.5884 7.736 35.5884 8.888V15.416C35.9884 15.72 36.5004 15.936 37.1244 16.064L37.0284 17H30.9564L31.0284 16.064C31.4764 15.968 31.8684 15.752 32.2044 15.416V9.104C32.2044 7.568 31.7164 6.8 30.7404 6.8C30.3084 6.8 29.8924 6.936 29.4924 7.208C29.1084 7.464 28.8044 7.808 28.5804 8.24C28.6124 8.528 28.6284 8.744 28.6284 8.888V15.416C28.9964 15.704 29.4764 15.92 30.0684 16.064L29.9724 17H23.9964L24.0684 16.064C24.5164 15.968 24.9084 15.752 25.2444 15.416V9.104C25.2444 7.568 24.7564 6.8 23.7804 6.8C23.3804 6.8 22.9884 6.92 22.6044 7.16C22.2204 7.4 21.9164 7.72 21.6924 8.12V15.416C22.0604 15.704 22.5404 15.92 23.1324 16.064L23.0364 17H16.9644L17.0364 16.016ZM43.7154 17.168C41.8594 17.168 40.4594 16.648 39.5154 15.608C38.5874 14.552 38.1234 13.112 38.1234 11.288C38.1234 9.384 38.6194 7.896 39.6114 6.824C40.6034 5.736 42.0354 5.192 43.9074 5.192C45.6994 5.192 46.9714 5.608 47.7234 6.44C48.4754 7.272 48.8994 8.512 48.9954 10.16L48.3234 11.168H41.5554V11.552C41.5554 12.896 41.7474 13.896 42.1314 14.552C42.5154 15.192 43.2354 15.512 44.2914 15.512C44.8994 15.512 45.4914 15.408 46.0674 15.2C46.6594 14.976 47.1714 14.64 47.6034 14.192L48.5634 15.128C47.9074 15.784 47.2194 16.288 46.4994 16.64C45.7954 16.992 44.8674 17.168 43.7154 17.168ZM45.5874 10.016C45.5874 8.736 45.4594 7.808 45.2034 7.232C44.9474 6.656 44.4834 6.368 43.8114 6.368C42.3554 6.368 41.6034 7.648 41.5554 10.208L45.5874 10.016ZM50.6441 21.08C50.9481 21.016 51.1801 20.944 51.3401 20.864C51.5161 20.784 51.7001 20.656 51.8921 20.48V8.504C51.8921 7.928 51.7801 7.536 51.5561 7.328C51.3481 7.104 50.9641 6.976 50.4041 6.944L50.4761 6.008C50.5881 5.976 50.7001 5.952 50.8121 5.936C50.9401 5.92 51.0521 5.904 51.1481 5.888C51.4201 5.84 51.9481 5.744 52.7321 5.6C53.5161 5.44 54.0921 5.304 54.4601 5.192L54.8921 5.408L55.1321 7.016C55.8201 5.8 56.9561 5.192 58.5401 5.192C59.9641 5.192 61.0601 5.672 61.8281 6.632C62.5961 7.592 62.9801 9 62.9801 10.856C62.9801 12.92 62.5161 14.488 61.5881 15.56C60.6761 16.632 59.3721 17.168 57.6761 17.168C57.1801 17.168 56.7481 17.12 56.3801 17.024C56.0281 16.928 55.6601 16.784 55.2761 16.592V20.48C55.7561 20.784 56.3481 21 57.0521 21.128L56.9561 22.064H50.5481L50.6441 21.08ZM56.8601 15.896C58.5881 15.896 59.4521 14.216 59.4521 10.856C59.4521 9.416 59.2921 8.352 58.9721 7.664C58.6521 6.976 58.1561 6.632 57.4841 6.632C56.7961 6.632 56.2521 6.968 55.8521 7.64C55.4681 8.312 55.2761 9.288 55.2761 10.568V15.416C55.4521 15.544 55.6841 15.656 55.9721 15.752C56.2601 15.848 56.5561 15.896 56.8601 15.896ZM64.4019 16.016C64.7059 15.952 64.9379 15.88 65.0979 15.8C65.2739 15.72 65.4579 15.592 65.6499 15.416V3.608C65.6499 3.016 65.5379 2.616 65.3139 2.408C65.1059 2.184 64.7219 2.056 64.1619 2.024L64.2339 1.088C64.4739 1.04 64.9059 0.967999 65.5299 0.871999C67.2419 0.583999 68.2739 0.383999 68.6259 0.271999L69.0579 0.511999V15.416C69.2019 15.544 69.4099 15.672 69.6819 15.8C69.9539 15.912 70.2499 16 70.5699 16.064L70.4979 17H64.3059L64.4019 16.016ZM74.9991 17.168C73.8951 17.168 73.0471 16.912 72.4551 16.4C71.8631 15.888 71.5671 15.144 71.5671 14.168C71.5671 13.128 71.9671 12.312 72.7671 11.72C73.5671 11.112 74.7431 10.752 76.2951 10.64L78.4071 10.496V9.104C78.4071 7.984 78.2711 7.208 77.9991 6.776C77.7431 6.344 77.2871 6.128 76.6311 6.128C76.1831 6.128 75.8071 6.248 75.5031 6.488C75.2151 6.712 75.0711 7 75.0711 7.352C75.0711 7.752 75.2471 8.088 75.5991 8.36C75.5191 8.76 75.3271 9.08 75.0231 9.32C74.7191 9.56 74.3511 9.68 73.9191 9.68C73.3911 9.68 72.9511 9.512 72.5991 9.176C72.2471 8.84 72.0711 8.424 72.0711 7.928C72.0711 7.08 72.4711 6.416 73.2711 5.936C74.0871 5.44 75.2071 5.192 76.6311 5.192C78.3271 5.192 79.6071 5.512 80.4711 6.152C81.3511 6.792 81.7911 7.736 81.7911 8.984V15.512C81.9031 15.64 82.0951 15.768 82.3671 15.896C82.6391 16.008 82.9271 16.096 83.2311 16.16L83.1351 17H78.8151L78.5751 15.632C78.0471 16.16 77.4951 16.552 76.9191 16.808C76.3431 17.048 75.7031 17.168 74.9991 17.168ZM76.1751 15.608C76.9111 15.608 77.6551 15.168 78.4071 14.288V11.672L77.2071 11.768C76.3271 11.832 75.6951 12.032 75.3111 12.368C74.9271 12.704 74.7351 13.216 74.7351 13.904C74.7351 14.448 74.8551 14.872 75.0951 15.176C75.3511 15.464 75.7111 15.608 76.1751 15.608ZM84.5364 16.016C84.8404 15.952 85.0724 15.88 85.2324 15.8C85.4084 15.72 85.6004 15.592 85.8084 15.416V8.504C85.8084 7.928 85.6964 7.536 85.4724 7.328C85.2484 7.104 84.8564 6.976 84.2964 6.944L84.3684 6.008L85.1604 5.864C86.8564 5.576 87.9604 5.352 88.4724 5.192L88.9044 5.408L89.0964 6.8C89.5284 6.288 90.0404 5.896 90.6324 5.624C91.2404 5.336 91.9044 5.192 92.6244 5.192C93.7124 5.192 94.5764 5.52 95.2164 6.176C95.8724 6.832 96.2004 7.736 96.2004 8.888V15.416C96.6004 15.72 97.1124 15.936 97.7364 16.064L97.6404 17H91.5684L91.6404 16.064C92.1204 15.968 92.5124 15.752 92.8164 15.416V9.104C92.8164 8.32 92.6884 7.744 92.4324 7.376C92.1924 6.992 91.8244 6.8 91.3284 6.8C90.9124 6.8 90.5044 6.92 90.1044 7.16C89.7204 7.4 89.4164 7.72 89.1924 8.12V15.416C89.5604 15.704 90.0404 15.92 90.6324 16.064L90.5364 17H84.4644L84.5364 16.016ZM99.0208 16.016C99.3248 15.952 99.5568 15.88 99.7168 15.8C99.8928 15.72 100.085 15.592 100.293 15.416V8.504C100.293 7.928 100.181 7.536 99.9568 7.328C99.7328 7.104 99.3408 6.976 98.7808 6.944L98.8528 6.008L99.6448 5.864C101.341 5.576 102.445 5.352 102.957 5.192L103.389 5.408L103.581 6.8C104.013 6.288 104.525 5.896 105.117 5.624C105.725 5.336 106.389 5.192 107.109 5.192C108.197 5.192 109.061 5.52 109.701 6.176C110.357 6.832 110.685 7.736 110.685 8.888V15.416C111.085 15.72 111.597 15.936 112.221 16.064L112.125 17H106.053L106.125 16.064C106.605 15.968 106.997 15.752 107.301 15.416V9.104C107.301 8.32 107.173 7.744 106.917 7.376C106.677 6.992 106.309 6.8 105.813 6.8C105.397 6.8 104.989 6.92 104.589 7.16C104.205 7.4 103.901 7.72 103.677 8.12V15.416C104.045 15.704 104.525 15.92 105.117 16.064L105.021 17H98.9488L99.0208 16.016ZM118.809 17.168C116.953 17.168 115.553 16.648 114.609 15.608C113.681 14.552 113.217 13.112 113.217 11.288C113.217 9.384 113.713 7.896 114.705 6.824C115.697 5.736 117.129 5.192 119.001 5.192C120.793 5.192 122.065 5.608 122.817 6.44C123.569 7.272 123.993 8.512 124.089 10.16L123.417 11.168H116.649V11.552C116.649 12.896 116.841 13.896 117.225 14.552C117.609 15.192 118.329 15.512 119.385 15.512C119.993 15.512 120.585 15.408 121.161 15.2C121.753 14.976 122.265 14.64 122.697 14.192L123.657 15.128C123.001 15.784 122.313 16.288 121.593 16.64C120.889 16.992 119.961 17.168 118.809 17.168ZM120.681 10.016C120.681 8.736 120.553 7.808 120.297 7.232C120.041 6.656 119.577 6.368 118.905 6.368C117.449 6.368 116.697 7.648 116.649 10.208L120.681 10.016ZM129.938 17.168C128.514 17.168 127.418 16.688 126.65 15.728C125.898 14.768 125.522 13.36 125.522 11.504C125.522 9.456 125.978 7.896 126.89 6.824C127.818 5.736 129.13 5.192 130.826 5.192C131.306 5.192 131.722 5.24 132.074 5.336C132.442 5.432 132.818 5.576 133.202 5.768V3.608C133.202 3.016 133.082 2.616 132.842 2.408C132.602 2.184 132.162 2.056 131.522 2.024L131.594 1.088C131.882 1.024 132.33 0.951999 132.938 0.871999C133.098 0.839999 133.578 0.759999 134.378 0.631999C135.194 0.503999 135.794 0.383999 136.178 0.271999L136.586 0.511999V15.512C136.73 15.64 136.93 15.768 137.186 15.896C137.458 16.008 137.738 16.096 138.026 16.16L137.93 17H133.61L133.298 15.44C132.61 16.592 131.49 17.168 129.938 17.168ZM131.018 15.728C131.722 15.728 132.258 15.368 132.626 14.648C133.01 13.912 133.202 12.824 133.202 11.384V6.944C133.026 6.816 132.794 6.712 132.506 6.632C132.234 6.536 131.946 6.488 131.642 6.488C129.898 6.488 129.026 8.16 129.026 11.504C129.026 12.944 129.186 14.008 129.506 14.696C129.826 15.384 130.33 15.728 131.018 15.728Z"
            />
          </svg>
        </Link>
        {/* Search */}
        {showAuthenticatedUI && (
          <div className="hidden min-w-[480px] lg:block xl:min-w-[580px]">
            <div className="search-input-wrapper relative h-[52px]">
              <div id="search-input-container" className="search-input-container">
                <svg
                  width={20}
                  height={20}
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="dark:fill-grey-80"
                    d="M8 0C12.4183 0 16 3.58172 16 8C16.0022 9.77666 15.4066 11.5028 14.3096 12.9004L19.71 18.29C19.8993 18.4778 20.0059 18.7334 20.0059 19C20.0059 19.2666 19.8993 19.5222 19.71 19.71C19.5222 19.8993 19.2666 20.0059 19 20.0059C18.7334 20.0059 18.4778 19.8993 18.29 19.71L12.9004 14.3096C11.5028 15.4066 9.77666 16.0022 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2Z"
                    fill="#4D4D4D"
                  />
                </svg>
                <input
                  id="searchInput"
                  type="text"
                  placeholder="Search sessions, people, or dates"
                  className="search-input"
                  autoComplete="off"
                />
                <button id="clearBtn" className="hidden">
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className="dark:fill-grey-80"
                      d="M4.92809 4.93004C8.85188 1.14032 15.0883 1.19477 18.9457 5.05211C22.803 8.90945 22.8574 15.1459 19.0677 19.0697C15.144 22.8594 8.9075 22.8049 5.05016 18.9476C1.19282 15.0903 1.13837 8.85383 4.92809 4.93004ZM17.6576 6.3402C14.5325 3.21856 9.46885 3.22015 6.34605 6.3441C3.22344 9.46804 3.2234 14.5317 6.34605 17.6556C9.46885 20.7796 14.5325 20.7812 17.6576 17.6595C19.1722 16.1656 20.0248 14.1268 20.0248 11.9994C20.0246 9.87215 19.1721 7.83402 17.6576 6.3402ZM14.8382 7.73961C15.0919 7.48617 15.4616 7.38703 15.808 7.47984C16.1544 7.57273 16.4251 7.8434 16.5179 8.1898C16.6107 8.53618 16.5116 8.90588 16.2582 9.15953L13.4076 11.9994L16.2582 14.8402C16.4473 15.0279 16.5541 15.2837 16.5541 15.5502C16.554 15.8167 16.4474 16.0724 16.2582 16.2601C16.0695 16.4472 15.8139 16.5516 15.5482 16.5502C15.2826 16.5517 15.0269 16.4471 14.8382 16.2601L11.9974 13.4095L9.15758 16.2601C8.96889 16.4471 8.71328 16.5517 8.44762 16.5502C8.18193 16.5516 7.92631 16.4472 7.73766 16.2601C7.54842 16.0724 7.44185 15.8167 7.44176 15.5502C7.44176 15.2837 7.54853 15.0279 7.73766 14.8402L10.5882 11.9994L7.73766 9.15953C7.48418 8.90588 7.38508 8.5362 7.47789 8.1898C7.57075 7.84338 7.84143 7.57271 8.18785 7.47984C8.53424 7.38703 8.90393 7.48613 9.15758 7.73961L11.9974 10.5902L14.8382 7.73961Z"
                      fill="#4D4D4D"
                    />
                  </svg>
                </button>
              </div>
              <div id="dropdown" className="search-dropdown hidden">
                <p className="border-grey-70 mx-4 border-t py-2 text-sm font-semibold">
                  Your previous search:
                </p>
                <ul className="h-full max-h-[114px] overflow-auto">
                  <li className="search-result-item">
                    <p className="text-grey-40 dark:text-[#B7B6B8]">
                      Search request #1
                    </p>
                  </li>
                  <li className="search-result-item">
                    <p className="text-grey-40 dark:text-[#B7B6B8]">
                      Search request #2
                    </p>
                  </li>
                  <li className="search-result-item">
                    <p className="text-grey-40 dark:text-[#B7B6B8]">
                      Search request #3
                    </p>
                  </li>
                  <li className="search-result-item">
                    <p className="text-grey-40 dark:text-[#B7B6B8]">
                      Search request #4
                    </p>
                  </li>
                  <li className="search-result-item">
                    <p className="text-grey-40 dark:text-grey-95">
                      Search request #5
                    </p>
                  </li>
                  <li className="search-result-item">
                    <p className="text-grey-40 dark:text-grey-95">
                      Search request #6
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        {/* Profile */}
        {showAuthenticatedUI && (
          <div className="ml-auto flex shrink-0 items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <button 
                  id="profile-dropdown-button"
                  className="flex items-center gap-1 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md p-1 transition-colors duration-200"
                  onClick={toggleDropdown}
                >
                  <div className="flex items-center gap-2">
                    <div className="relative flex size-8 items-center justify-center rounded-full bg-[#367D7D]">
                      <span className="text-xs font-bold text-white">TN</span>
                      <div className="notification" />
                    </div>
                    <h4 className="dark:text-grey-80 hidden lg:block"># InstructorName</h4>
                  </div>
                  <div className="hidden px-2 lg:block">
                    <svg
                      width={11}
                      height={6}
                      viewBox="0 0 11 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    >
                      <path
                        className="fill-grey-30 dark:fill-grey-80"
                        d="M0.295417 0.294821C0.687449 -0.0944511 1.32107 -0.0921512 1.71046 0.299704L5.00049 3.57998L8.3003 0.299704C8.4894 0.111707 8.74655 0.00778684 9.01319 0.00966458C9.27966 0.011659 9.5339 0.119916 9.72022 0.310446C10.1079 0.700483 10.1079 1.32959 9.72022 1.71963L5.72022 5.71963C5.52742 5.90562 5.2683 6.00678 5.00049 5.9999C4.73475 6.00139 4.47921 5.89701 4.29053 5.70986L0.290534 1.70986C-0.0986692 1.31773 -0.0966323 0.684134 0.295417 0.294821Z"
                      />
                    </svg>
                  </div>
                </button>
                
                {/* Animated Dropdown */}
                {showAuthenticatedUI && (
                  <div
                    id="profile-dropdown"
                    className={`absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50 transition-all duration-200 origin-top-right ${
                      isDropdownOpen 
                        ? 'opacity-100 scale-100 visible' 
                        : 'opacity-0 scale-95 invisible'
                    }`}
                  >
                    <div className="py-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-colors duration-200"
                      >
                        <svg
                          width={16}
                          height={16}
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            className="fill-current"
                            d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
                          />
                        </svg>
                        {t("header.logout")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/*  Mobile Menu */}
        <div className="lg:hidden">
          {/* Mobile Menu container */}
          <div
            id="mobile-menu"
            className="dark:bg-primary-20 fixed top-0 left-0 z-10 h-full w-[300px] -translate-x-full transform bg-white shadow-xl transition-transform duration-300 ease-in-out [.is-open]:visible [.is-open]:opacity-100"
          >
            {showAuthenticatedUI && (
              <div className="flex h-full w-full flex-col">
                {/* Profile */}
                <button className="bg-grey-95 dark:bg-primary-10 dark:border-primary-10 border-grey-95 flex items-center justify-between border p-4 pl-6">
                  <div className="flex items-center gap-2">
                    <div className="relative flex size-12 items-center justify-center rounded-full border border-[#367D7D] bg-[#367D7D] dark:border-white">
                      <span className="font-bold text-white">TN</span>
                    </div>
                    <div className="flex flex-col items-start">
                      <h4 className="">Hello, #InstructorName</h4>
                      <p className="text-sm">instructor account</p>
                    </div>
                  </div>
                  <svg
                    width={10}
                    height={20}
                    viewBox="0 0 10 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className="dark:fill-white"
                      d="M9.74867 10.66L1.74867 19.66C1.38391 20.0737 0.753068 20.114 0.338672 19.75C-0.0750326 19.3852 -0.115299 18.7544 0.248672 18.34L7.65867 10L0.248672 1.66C-0.0871302 1.24308 -0.0350415 0.635662 0.36684 0.282006C0.768721 -0.0716495 1.37783 -0.0460871 1.74867 0.339997L9.74867 9.34C10.0803 9.7175 10.0803 10.2825 9.74867 10.66Z"
                      fill="#4D4D4D"
                    />
                  </svg>
                </button>
                {/* Menu */}
                <div className="px-6 py-2">
                  <nav className="w-full flex-1" role="menu">
                    <ul className="space-y-4">
                      <li>
                        <a
                          href="#"
                          className="mobile-menu-link group/link active"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(ROUTES.HOME_PAGE);
                          }}
                        >
                          <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              className="fill-grey-50"
                              d="M11.3387 1.24963C11.7161 0.91842 12.2807 0.918413 12.658 1.24963L19.1483 6.99963L19.9979 7.73987L21.658 9.24963C22.0717 9.61439 22.1119 10.2454 21.7479 10.6598C21.3831 11.0734 20.7531 11.1136 20.3387 10.7496L19.9979 10.4498V18.9996C19.9979 20.6565 18.6547 21.9996 16.9979 21.9996H13.9979C13.4458 21.9994 12.9979 21.5518 12.9979 20.9996V15.9996C13.002 15.7394 12.8976 15.4893 12.7088 15.3102C12.5234 15.1156 12.2666 15.0034 11.9979 14.9996C11.4459 14.9999 10.9981 15.4477 10.9979 15.9996V20.9996C10.9979 21.5519 10.5502 21.9996 9.99788 21.9996H6.99788C5.34125 21.9994 3.99788 20.6563 3.99788 18.9996V10.4498L3.65804 10.7496C3.24112 11.0854 2.63376 11.0333 2.28011 10.6315C1.92676 10.2296 1.95272 9.62037 2.3387 9.24963L3.99788 7.73987L4.84847 6.99963L11.3387 1.24963ZM5.99788 8.66956V18.9996C5.99788 19.5518 6.44582 19.9994 6.99788 19.9996H8.99788V15.9996C8.99808 14.3431 10.3414 12.9999 11.9979 12.9996C13.6546 12.9996 14.9977 14.3429 14.9979 15.9996V19.9996H16.9979C17.5502 19.9996 17.9979 19.5519 17.9979 18.9996V8.66956L11.9979 3.34045L5.99788 8.66956Z"
                            />
                          </svg>
                          Home
                        </a>
                      </li>
                      <li>
                        <a
                          href="#"
                          className="mobile-menu-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(ROUTES.SESSIONS);
                          }}
                        >
                          <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7 2C7.55228 2 8 2.44772 8 3V4H16V3C16 2.44772 16.4477 2 17 2C17.5523 2 18 2.44772 18 3V4H19C20.6569 4 22 5.34315 22 7V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V7C2 5.34315 3.34315 4 5 4H6V3C6 2.44772 6.44772 2 7 2ZM19 10H5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10ZM16 12C16.5523 12 17 12.4477 17 13V15C17 15.5523 16.5523 16 16 16H14C13.4477 16 13 15.5523 13 15V13C13 12.4477 13.4477 12 14 12H16ZM16 6H6C5.44772 6 5 6.44772 5 7V8H19V7C19 6.44772 18.5523 6 18 6H16Z"
                              fill="#808080"
                            />
                          </svg>
                          Sessions
                        </a>
                      </li>
                    </ul>
                  </nav>
                  <hr className="border-grey-70 my-2" />
                  {/* Theme Toggle Section */}
                  <div className="px-4 py-3">
                    <ThemeToggle
                      mode={themeMode}
                      onChange={handleThemeChange}
                    />
                  </div>
                  <div className="mt-6 flex shrink-0 justify-center">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate('/create-session')}
                    >
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          className="fill-current"
                          d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z"
                        />
                      </svg>
                      <span>New Session</span>
                    </button>
                  </div>
                  {showAuthenticatedUI && (
                    <div className="mt-4 px-2">
                      <button
                        onClick={() => {
                          handleLogout();
                          // Close mobile menu
                          const mobileMenu = document.getElementById('mobile-menu');
                          const overlay = document.getElementById('mobile-menu-overlay');
                          if (mobileMenu && overlay) {
                            mobileMenu.classList.remove('translate-x-0');
                            mobileMenu.classList.add('-translate-x-full');
                            overlay.classList.remove('opacity-100', 'visible');
                            overlay.classList.add('opacity-0', 'invisible');
                          }
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-md transition-colors duration-200 border border-red-200 dark:border-red-800"
                      >
                        <svg
                          width={16}
                          height={16}
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            className="fill-current"
                            d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
                          />
                        </svg>
                        {t("header.logout")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Mobile Menu Overlay */}
          <div
            id="mobile-menu-overlay"
            className="pointer-events-none fixed inset-0 z-0 bg-black/80 opacity-0 transition-opacity duration-300 ease-in-out"
          >
            {/* Menu Close */}
            <button
              className="pointer-events-none absolute top-4 right-4 z-10"
              id="menu-close-btn"
            >
              <img src="/assets/images/icons/menu-close.svg" alt="img" />
            </button>
          </div>
        </div>
      </div>
    </header>

  )
}

export default Header
